// S3 — shared realtime notification socket (namespace "/notifications").
//
// One ref-counted socket serves every useNotifications() instance (the Nav
// bell and /inbox can mount the hook simultaneously) — a naive per-hook
// disconnect() would tear the other consumer's connection down (the same
// latent hazard Messages.tsx vs useChatUnread carry on the chat socket).
// The server verifies the access JWT on the handshake and joins the socket to
// its own user room; the only inbound event is a content-free
// "notification:new" nudge (consumers refetch over the REST inbox API).
// socket.io-client is imported dynamically so it never loads on the
// public/marketing shell.
import { sessionStore } from "../auth/session-store";

export interface NotificationsSocketHandlers {
  onNudge?: () => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
}

interface NotifSocket {
  connected: boolean;
  on: (ev: string, fn: (...a: unknown[]) => void) => void;
  disconnect: () => void;
  auth: Record<string, unknown>;
  io: { on: (ev: string, fn: (...a: unknown[]) => void) => void };
}

const subscribers = new Set<NotificationsSocketHandlers>();
let socket: NotifSocket | null = null;
let connecting = false;

const fanOut = (call: (h: NotificationsSocketHandlers) => void): void => {
  for (const h of subscribers) {
    try {
      call(h);
    } catch {
      // one consumer's handler must not break the others
    }
  }
};

const ensureSocket = async (): Promise<void> => {
  if (socket || connecting) return;
  connecting = true;
  try {
    const { io } = await import("socket.io-client");
    if (subscribers.size === 0) return; // everyone left while the chunk loaded
    const s = io("/notifications", {
      path: "/api/socket.io",
      auth: { token: sessionStore.get()?.accessToken ?? "" },
      transports: ["websocket", "polling"],
    });
    // The access token rotates — hand every reconnect attempt a fresh one
    // (same pattern LiveSession uses), otherwise a long-lived tab reconnects
    // with an expired token and the server keeps dropping it.
    s.io.on("reconnect_attempt", () => {
      s.auth = { token: sessionStore.get()?.accessToken ?? "" };
    });
    s.on("connect", () => fanOut((h) => h.onConnect?.()));
    s.on("disconnect", () => fanOut((h) => h.onDisconnect?.()));
    s.on("notification:new", () => fanOut((h) => h.onNudge?.()));
    socket = s as unknown as NotifSocket;
  } catch {
    // client chunk failed to load — the hook's polling fallback covers this
  } finally {
    connecting = false;
  }
};

/** True while the shared socket is live (the hook suppresses polling then). */
export const isNotificationsSocketConnected = (): boolean => socket?.connected ?? false;

/**
 * Subscribe to the shared notifications socket. Returns an unsubscribe fn;
 * the underlying connection is created with the first subscriber and torn
 * down with the last (logout unmounts every subscriber → full teardown).
 */
export const subscribeNotificationsSocket = (
  handlers: NotificationsSocketHandlers,
): (() => void) => {
  subscribers.add(handlers);
  void ensureSocket();
  return () => {
    subscribers.delete(handlers);
    if (subscribers.size === 0 && socket) {
      socket.disconnect();
      socket = null;
    }
  };
};
